"use client";

import { Mail } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

export function ForgotPasswordForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const t = useTranslations("auth");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  const emailSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(t("errors.invalidEmail")),
      }),
    [t],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const validation = emailSchema.safeParse({ email });
    if (!validation.success) {
      setError(validation.error.issues[0].message);
      setIsLoading(false);
      return;
    }

    try {
      const result = await authClient.requestPasswordReset({
        email: validation.data.email,
        redirectTo: "/reset-password",
      });
      // BetterAuth returns success even if email doesn't exist (security)
      if (result.error) {
        throw new Error(result.error.message);
      }
      setEmailSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.generic"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          {emailSent ? (
            <div className="flex flex-col items-center justify-center gap-6 p-6 md:p-8">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">{t("forgot.sentTitle")}</h1>
                <p className="text-muted-foreground text-sm text-balance max-w-sm">
                  {t("forgot.sentBodyBefore")}
                  <strong>{email}</strong>
                  {t("forgot.sentBodyAfter")}
                </p>
              </div>
              <div className="mt-4 text-center">
                <Link
                  href="/login"
                  className="text-primary underline-offset-2 hover:underline text-sm"
                >
                  {t("forgot.back")}
                </Link>
              </div>
            </div>
          ) : (
            <form className="p-6 md:p-8" onSubmit={handleSubmit}>
              <FieldGroup>
                <div className="flex flex-col items-center gap-2 text-center">
                  <h1 className="text-2xl font-bold">{t("forgot.heading")}</h1>
                  <p className="text-muted-foreground text-sm text-balance">
                    {t("forgot.description")}
                  </p>
                </div>
                {error && (
                  <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
                <Field>
                  <FieldLabel htmlFor="email">
                    {t("forgot.emailLabel")}
                  </FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("forgot.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </Field>
                <Field>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? t("forgot.submitPending") : t("forgot.submit")}
                  </Button>
                </Field>
                <FieldDescription className="text-center">
                  {t("forgot.rememberBefore")}
                  <Link
                    href="/login"
                    className="underline-offset-2 hover:underline"
                  >
                    {t("forgot.rememberLink")}
                  </Link>
                </FieldDescription>
              </FieldGroup>
            </form>
          )}
          <div className="bg-muted relative hidden md:block">
            <img
              src="/multic_icon.png"
              alt={t("logoAlt")}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
